import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import "./MappingPage.css";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE } from "../api/config";

// ---- helpers: sanitize mapping & safe stringify to avoid cyclic structures ----
const sanitizeMapping = (input, allowedKeys = null) => {
  try {
    const out = {};
    if (!input || typeof input !== "object") return out;
    const keys = Array.isArray(allowedKeys) && allowedKeys.length
      ? allowedKeys
      : Object.keys(input);
    for (const k of keys) {
      const v = input[k];
      if (v == null) { out[k] = ""; continue; }
      switch (typeof v) {
        case "string":
        case "number":
        case "boolean":
          out[k] = String(v);
          break;
        case "object":
          // Accept common shapes like { value: "..." }
          if (v && typeof v.value === "string") {
            out[k] = v.value;
          } else {
            out[k] = ""; // drop non-serializable values
          }
          break;
        default:
          out[k] = "";
      }
    }
    return out;
  } catch (e) {
    console.warn("sanitizeMapping failed:", e);
    return {};
  }
};

const safeStringify = (obj) => {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) return undefined; // prevent cycles
      seen.add(value);

      // Filter File/Blob/Synthetic-like objects just in case
      if (typeof File !== "undefined" && value instanceof File) return undefined;
      if (typeof Blob !== "undefined" && value instanceof Blob) return undefined;
    }
    if (typeof value === "function") return undefined;
    return value;
  });
};

// --- Polling helpers for waiting until Excel-Daten exist ---
// Heuristik: Kontocode aus Excel-Dateiname bestimmen ("005 900", "006", "HIG HSP", "05A" ...)
const inferKontocode = (name) => {
  const up = (name || "").toUpperCase();
  // 1) Zweiteiliger Code wie "005 900" oder "HIG HSP"
  let m = up.match(/\b[0-9]{3}\s[0-9]{3}\b/);
  if (m) return m[0];
  m = up.match(/\b[A-Z]{3}\s[A-Z]{3}\b/);
  if (m) return m[0];
  // 2) Einteiliger 3er-Code (alphanumerisch)
  m = up.match(/\b[A-Z0-9]{3}\b/);
  if (m) return m[0];
  return "";
};
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

const pollSessionForExcel = async (sessionId, tries = 600, delay = 500) => {
  // up to ~5 Minuten warten (600 * 500ms)
  for (let i = 0; i < tries; i++) {
    try {
      const r = await fetch(`${API_BASE}/session/data`, {
        headers: { "Session-ID": sessionId, "Cache-Control": "no-cache" },
      });
      if (r.ok) {
        const d = await r.json();
        if (d && d.csv && d.excel) return d;
      }
    } catch (e) {
      // ignore transient fetch errors during polling
    }
    await sleep(delay);
  }
  return null;
};


const MappingPage = ({ refreshTrigger, initialMode }) => {
  const location = useLocation();
  const [csvData, setCsvData] = useState(null);
  const [excelData, setExcelData] = useState(null);
  const [mappingData, setMappingData] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [isAccountMatch, setIsAccountMatch] = useState(false);
  const [finalMappings, setFinalMappings] = useState({});
  const [loading, setLoading] = useState(false); 
  const [showPopup, setShowPopup] = useState(false);
  const username = localStorage.getItem("user") || "user";
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const defaultFileName = `${username}_Export_${timestamp}.xlsx`;
  const [fileName, setFileName] = useState(defaultFileName);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [autoSkipInfo, setAutoSkipInfo] = useState("");
  const [skipMappingUI, setSkipMappingUI] = useState(false);
  const [pendingBlob, setPendingBlob] = useState(null);
  const [toastMsg, setToastMsg] = useState("");
  const [waitingForExcel, setWaitingForExcel] = useState(false);

  // Guards to prevent duplicate polling and to stop when unmounted
  const pollingRef = useRef(false);
  const mountedRef = useRef(true);
  const modeLockedRef = useRef(false);
  // Verhindert mehrfaches Anfordern des Mappings, sobald CSV+Excel vorhanden sind
  const mappingRequestedRef = useRef(false);
  // Throttle for /session/data to prevent spam & loader flicker
  const lastSessionFetchRef = useRef(0);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // ===== Batch (Massenverarbeitung) state =====
  // ===== View Mode: Einzel / Batch =====
  // Resolve initial view mode from navigation key, prop, URL, or localStorage
  const computedInitialMode = (() => {
    // 1) Prüfe Navigation-Key (1=Einzel, 2=Batch)
    try {
      const navKeyFromState = location && location.state && typeof location.state.modeKey !== 'undefined' ? Number(location.state.modeKey) : null;
      const navKeyFromLS = (() => { const v = localStorage.getItem('navModeKey'); return v != null ? Number(v) : null; })();
      const navKey = (navKeyFromState != null ? navKeyFromState : navKeyFromLS);
      if (navKey === 2) return 'batch';
      if (navKey === 1) return 'single';
    } catch (e) {}

    // 2) Falls per Prop erzwungen
    if (initialMode === 'batch' || initialMode === 'single') return initialMode;

    // 3) URL-Parameter
    try {
      const params = new URLSearchParams(location.search || "");
      const m = (params.get("mode") || "").toLowerCase();
      if (m === 'batch' || m === 'single') return m;

      // 4) Letzte Präferenz
      const pref = localStorage.getItem('preferredViewMode');
      if (pref === 'batch') return 'batch';
    } catch (e) {}

    // 5) Default
    return 'single';
  })();

  const [viewMode, setViewMode] = useState(computedInitialMode); // 'single' | 'batch'
  const isBatchMode = viewMode === 'batch';

  // Lock when initial mode is forced via prop or URL/pref says batch
  useEffect(() => {
    if (initialMode === 'batch' || initialMode === 'single' || computedInitialMode === 'batch') {
      modeLockedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMode]);

  // Beim Wechsel des View-Modus (single/batch) erneutes Mapping-Laden erlauben
  useEffect(() => {
    mappingRequestedRef.current = false;
  }, [viewMode]);

  // Lock and apply nav key if present in navigation state or localStorage
  useEffect(() => {
    try {
      const raw = location && location.state && typeof location.state.modeKey !== 'undefined' ? Number(location.state.modeKey) : null;
      const lsVal = (() => { const v = localStorage.getItem('navModeKey'); return v != null ? Number(v) : null; })();
      const key = (raw != null ? raw : lsVal);
      if (key === 2) {
        modeLockedRef.current = true;
        setViewMode('batch');
        try { localStorage.setItem('preferredViewMode','batch'); } catch(e) {}
      } else if (key === 1) {
        modeLockedRef.current = false; // Einzel darf umschalten
        setViewMode('single');
        try { localStorage.setItem('preferredViewMode','single'); } catch(e) {}
      }
    } catch (_) {}
  }, [location]);

  // Batch-Datei-Listen (müssen vor dem Umschalt-Effect deklariert sein)
  const [batchResults, setBatchResults] = useState([]); // [{csv, excel, status, message}]

  // Umschalten Einzel/Batch, wenn vorhandene Einzelsession besteht
  useEffect(() => {
    if (modeLockedRef.current) return;
    const hasSingleSession = !!(csvData && excelData);
    if (hasSingleSession) {
      setViewMode('single');
    }
  }, [csvData, excelData]);
  // Keep view mode in sync with localStorage and refresh triggers
  useEffect(() => {
    const applyPref = () => {
      const pref = localStorage.getItem('preferredViewMode');
      const navKey = Number(localStorage.getItem('navModeKey') || '');
      const hasSingleSession = !!(csvData && excelData);

      if (pref === 'batch' || navKey === 2) {
        modeLockedRef.current = true;
        setViewMode('batch');
      } else if (pref === 'single' || hasSingleSession) {
        modeLockedRef.current = false;
        setViewMode('single');
      } else {
        setViewMode('single');
      }
    };
    applyPref();
    const onStorage = () => applyPref();
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshTrigger, csvData, excelData, location]);
  const [batchZipBlob, setBatchZipBlob] = useState(null);
  const [batchZipName, setBatchZipName] = useState("batch_result.zip");
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ total: 0, done: 0, messages: [] });
  const progressTimerRef = useRef(null);

  // Allow explicit mode switching via URL query, e.g. /mapping?mode=batch or ?mode=single
  useEffect(() => {
    if (initialMode === 'batch' || initialMode === 'single') return; // do not override explicit prop
    try {
      const params = new URLSearchParams(location.search || "");
      const m = (params.get("mode") || "").toLowerCase();
      if (m === "batch") {
        modeLockedRef.current = true;
        setViewMode("batch");
      } else if (m === "single") {
        modeLockedRef.current = false;
        setViewMode("single");
      }
    } catch (e) {
      // ignore URL parse errors
    }
  }, [location.search, initialMode]);


  const showToast = (msg) => {
    try { setToastMsg(msg); } catch(e) {}
    setTimeout(() => setToastMsg(""), 3000);
  };

  const navigate = useNavigate();

  // ===== Batch (Massenverarbeitung) helpers =====
  const handleFinalMapping = async (mappingsOverride = null) => {
    const sessionId = localStorage.getItem("sessionId");
    const raw = mappingsOverride || finalMappings;
    if (!sessionId || !raw) return;

    // Payload aus aktuellem State sammeln (trimmen, Standard-Formel setzen)
    const payload = collectCurrentMappings();
    const nonEmpty = Object.values(payload).filter((v) => (v || "").trim() !== "").length;
    console.debug("[MappingPage] sending mappings → keys:", Object.keys(payload).length, "nonEmpty:", nonEmpty, payload);

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/session/insert_data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Session-ID": sessionId,
        },
        body: safeStringify({ mappings: payload }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(data.detail || "Fehler beim Einfügen der Daten.");
        return;
      }

      // Blob vorbereiten und Dateiname setzen
      const blob = await response.blob();
      setPendingBlob(blob);
      const contentDisposition = response.headers.get("Content-Disposition");
      let suggestedFileName = "updated_file.xlsx";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+?)"/);
        if (match && match[1]) suggestedFileName = match[1];
      }
      setFileName(suggestedFileName);
      setShowPopup(true);
    } catch (error) {
      console.error("Fehler beim Vorbereiten der Datei:", error);
      setSessionError("Netzwerkfehler: Datei konnte nicht vorbereitet werden.");
    } finally {
      setLoading(false);
    }
  };

  const handleMapping = useCallback(async () => {
    const sessionId = localStorage.getItem("sessionId");
    if (!sessionId) return;

    // In der Batch-Ansicht nur fortfahren, wenn sowohl CSV als auch Excel im State stehen
    if (isBatchMode && (!csvData || !excelData)) {
      return; // Batch-Tabelle soll weiterhin sichtbar bleiben
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/session/mapping`, {
        method: "POST",
        headers: { "Session-ID": sessionId },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (isBatchMode) {
          // In der Batch-Ansicht nur als Hinweis zeigen, UI nicht blockieren
          showToast(errorData.detail || 'Zuordnung aktuell nicht möglich – bitte zuerst CSV & Excel aktivieren.');
          return;
        }
        setSessionError(errorData.detail || "Fehler beim Abrufen der Zuordnungsdaten.");
        return;
      }

      const data = await response.json();

      // Reset infos
      setAutoSkipInfo("");

      // Auto-Skip: gespeichertes Mapping vorhanden?
      if (data.savedMapping && Object.keys(data.savedMapping).length > 0) {
        const sanitized = sanitizeMapping(data.savedMapping, data.excelHeaders);
        setFinalMappings(sanitized);
        setSkipMappingUI(true);
        setAutoSkipInfo("Gespeichertes Mapping gefunden – Zuordnung wird automatisch angewendet.");
        showToast("Gespeichertes Mapping gefunden – Zuordnung wird automatisch angewendet.");
        await handleFinalMapping(sanitized);
        return;
      }

      // Kein gespeichertes Mapping → UI anzeigen & Initialvorschläge
      setSkipMappingUI(false);
      setMappingData(data);

      const initialMappings = {};
      data.excelHeaders.forEach((header) => {
        initialMappings[header] =
          data.csvHeaders.find((csvHeader) => csvHeader === header) || "";
      });
      setFinalMappings(initialMappings);
      
    } catch (error) {
      console.error("Fehler beim Abrufen der Zuordnungsdaten:", error);
      setSessionError("Netzwerkfehler: Zuordnungsdaten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [isBatchMode, csvData, excelData, handleFinalMapping]);

  const fetchBatchList = useCallback(async () => {
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) { showToast('Keine Session-ID – bitte neu starten.'); return []; }
    try {
      setBatchRunning(true);
      const exResp = await fetch(`${API_BASE}/excel/multi/list`, {
        headers: { 'Session-ID': sessionId, 'Cache-Control': 'no-cache' },
      });
      const excelJson = exResp.ok ? await exResp.json() : { files: [] };
      const excelFiles = Array.isArray(excelJson.files) ? excelJson.files : [];

      let csvFiles = [];
      try {
        const csvResp = await fetch(`${API_BASE}/csv/multi/list`, {
          headers: { 'Session-ID': sessionId, 'Cache-Control': 'no-cache' },
        });
        if (csvResp.ok) {
          const csvJson = await csvResp.json();
          csvFiles = Array.isArray(csvJson.files) ? csvJson.files : [];
        }
      } catch(_) {}

      const normalizeNum = (s) => (s || '').replace(/\D/g, '');

      const rows = await Promise.all(excelFiles.map(async (ex) => {
        const excelName = ex.fileName || ex.name || '';
        const sheet = ex.chosenSheet || ex.sheetName || '';
        const acc = ex.accountNumber || ex.konto || '';
        const accN = normalizeNum(acc);

        const csvMatch = csvFiles.find(c => normalizeNum(c.konto || c.accountNumber) === accN);
        const kontocode = (csvMatch && csvMatch.kontocode) ? csvMatch.kontocode : inferKontocode(excelName);

        let mappingExists = false;
        try {
          const params = new URLSearchParams({
            schema: 'C',
            excel_name: excelName,
            sheet_name: sheet,
            kontocode: kontocode || '',
            account_number: acc || '',
          });
          const chk = await fetch(`${API_BASE}/mapping/exists_for?${params.toString()}`, {
            headers: { 'Session-ID': sessionId },
          });
          if (chk.ok) {
            const j = await chk.json();
            mappingExists = !!j.exists;
          }
        } catch (_) {}

        return {
          excel: excelName || '-',
          csv: csvMatch ? (csvMatch.fileName || csvMatch.name) : '-',
          kontocode: kontocode || '',
          excel_account: acc || '',
          csv_account: csvMatch ? (csvMatch.konto || csvMatch.accountNumber || '') : '',
          excel_newest: ex.newestDate || ex.neuestes_datum || '',
          excel_oldest: ex.oldestDate || ex.aeltestes_datum || '',
          csv_newest: csvMatch ? (csvMatch.newest_date || csvMatch.neuestes_datum || '') : '',
          csv_oldest: csvMatch ? (csvMatch.oldest_date || csvMatch.aeltestes_datum || '') : '',
          csv_found: !!csvMatch,
          mapping_found: mappingExists,
          account_match: !!(accN && csvMatch && normalizeNum(csvMatch.konto || csvMatch.accountNumber) === accN),
        };
      }));

      setBatchResults(rows);
      showToast(`Liste aktualisiert: ${rows.length} Excel-Dateien.`);
      return rows;
    } catch (e) {
      console.error('fetchBatchList error:', e);
      showToast('Konnte Liste nicht laden.');
      return [];
    } finally {
      setBatchRunning(false);
    }
  }, []);

  const handleBatchProcess = async () => {
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) { showToast('Keine Session-ID – bitte neu starten.'); return; }

    try {
      // Liste frisch holen und direkt verwenden (nicht den evtl. noch alten State)
      const rows = await fetchBatchList();

      setBatchRunning(true);
      setBatchProgress({ total: rows.length, done: 0, messages: [] });

      // Progress-Polling starten
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      progressTimerRef.current = setInterval(async () => {
        try {
          const pr = await fetch(`${API_BASE}/batch/progress`, {
            headers: { 'Session-ID': sessionId, 'Cache-Control': 'no-cache' },
          });
          if (pr.ok) {
            const j = await pr.json();
            setBatchProgress(j);
          }
        } catch (_) {}
      }, 1000);

      // Nur minimal nötige Felder schicken
      const payloadRows = rows.map(r => ({
        excel: r.excel || '',
        csv: r.csv || '',
        excel_account: r.excel_account || '',
        csv_account: r.csv_account || '',
        kontocode: r.kontocode || '',
        mapping_found: !!r.mapping_found,
        account_match: !!r.account_match,
      }));

      const resp = await fetch(`${API_BASE}/batch/process`, {
        method: 'POST',
        headers: { 'Session-ID': sessionId, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: payloadRows }),
      });

      const ct = resp.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const json = await resp.json();
        if (Array.isArray(json.results)) setBatchResults(json.results);
        if (json.zip_base64) {
          const bstr = atob(json.zip_base64);
          const u8 = new Uint8Array(bstr.length);
          for (let i = 0; i < bstr.length; i++) u8[i] = bstr.charCodeAt(i);
          const blob = new Blob([u8], { type: 'application/zip' });
          setBatchZipBlob(blob);
          const name = json.zip_name || 'batch_result.zip';
          setBatchZipName(name);
          downloadBatchZip(blob, name);
        }
        if (json.message) showToast(json.message);
      } else {
        const blob = await resp.blob();
        setBatchZipBlob(blob);
        const cd = resp.headers.get('Content-Disposition');
        const m = cd && cd.match(/filename="(.+?)"/);
        const name = (m && m[1]) ? m[1] : 'batch_result.zip';
        setBatchZipName(name);
        downloadBatchZip(blob, name);
        showToast('Massenverarbeitung abgeschlossen – ZIP bereit.');
      }
    } catch (err) {
      console.error('Batch-Fehler:', err);
      showToast('Fehler bei der Massenverarbeitung.');
    } finally {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      setBatchRunning(false);
    }
  };

  const downloadBatchZip = (blobArg = null, nameArg = null) => {
    const blob = blobArg || batchZipBlob;
    if (!blob) return;
    const fileName = nameArg || batchZipName || 'batch_result.zip';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // — Unified submit: decides between single and batch —
  useEffect(() => {
    if (isBatchMode) {
      fetchBatchList();
    }
  }, [isBatchMode, refreshTrigger, fetchBatchList]);
  const handleUnifiedSubmit = async () => {
    try {
      // Wenn die Batch-Ansicht aktiv ist, immer die Batch-Verarbeitung starten
      if (isBatchMode) {
        try { localStorage.setItem('preferredViewMode','batch'); localStorage.setItem('navModeKey','2'); } catch(e) {}
        modeLockedRef.current = true;
        setViewMode('batch');
        await handleBatchProcess();
        return;
      }

      // Einzel-Verarbeitung (wie gehabt)
      const hasSingleSession = !!(csvData && excelData);
      if (hasSingleSession) {
        modeLockedRef.current = false;
        try { localStorage.setItem('preferredViewMode','single'); localStorage.setItem('navModeKey','1'); } catch(e) {}
        setViewMode('single');
        await handleMapping();
      } else {
        showToast('Bitte zuerst CSV &amp; Excel laden (Einzel) oder zur Massenverarbeitung wechseln.');
      }
    } catch (e) {
      console.error('Unified submit error:', e);
      showToast('Fehler bei der Übermittlung.');
    }
  };

  // --- Batch: Ready-Check und Navigation ---
  const handleBatchProceed = async () => {
    try {
      try { localStorage.setItem('navModeKey','2'); localStorage.setItem('preferredViewMode','batch'); } catch(e) {}
      modeLockedRef.current = true;
      setViewMode('batch');

      const sessionId = localStorage.getItem('sessionId');
      if (!sessionId) {
        showToast('Keine Session-ID – bitte neu starten.');
        return;
      }

      // 1) Prüfen, ob alle Sheets gewählt sind
      const readyResp = await fetch(`${API_BASE}/excel/multi/ready`, {
        headers: { 'Session-ID': sessionId, 'Cache-Control': 'no-cache' },
      });
      if (!readyResp.ok) {
        showToast('Fehler beim Prüfen der Excel-Auswahl.');
        return;
      }
      const readyJson = await readyResp.json();
      if (!readyJson.ready) {
        const hint = readyJson.missing && readyJson.missing.length
          ? `Fehlend: ${readyJson.missing.slice(0, 5).join(', ')}${readyJson.missing.length > 5 ? ' …' : ''}`
          : 'Bitte alle Sheets wählen.';
        showToast(`Noch nicht bereit. ${hint}`);
        return;
      }

      // 2) Listen holen
      const [exListResp, csvListResp] = await Promise.all([
        fetch(`${API_BASE}/excel/multi/list`, { headers: { 'Session-ID': sessionId, 'Cache-Control': 'no-cache' } }),
        fetch(`${API_BASE}/csv/multi/list`,   { headers: { 'Session-ID': sessionId, 'Cache-Control': 'no-cache' } }),
      ]);
      if (!exListResp.ok) { showToast('Konnte Excel-Liste nicht abrufen.'); return; }
      const exListJson = await exListResp.json();
      const excelFiles = Array.isArray(exListJson.files) ? exListJson.files : [];

      let csvFiles = [];
      try {
        if (csvListResp.ok) {
          const csvListJson = await csvListResp.json();
          csvFiles = Array.isArray(csvListJson.files) ? csvListJson.files : [];
        }
      } catch(_) {}

      // 3) Erste Excel mit gewähltem Sheet bestimmen
      const first = excelFiles.find(x => x && (x.chosenSheet || x.sheetName));
      if (!first) { showToast('Kein Eintrag mit gewähltem Sheet gefunden.'); return; }

      // 4) Passende CSV anhand Kontonummer heuristisch suchen
      const normalizeNum = (s) => (s || '').toString().replace(/\D/g, '');
      const excelAcc = first.accountNumber || first.konto || '';
      const excelAccN = normalizeNum(excelAcc);
      let matchedCsv = null;
      if (excelAccN) {
        matchedCsv = csvFiles.find(c => normalizeNum(c.konto || c.accountNumber) === excelAccN) || null;
      }

      // 5) Excel in Session aktivieren
      const fdExcel = new FormData();
      fdExcel.append('file_name', first.fileName || first.name);
      const actExcel = await fetch(`${API_BASE}/excel/multi/activate`, {
        method: 'POST',
        headers: { 'Session-ID': sessionId },
        body: fdExcel,
      });
      if (!actExcel.ok) {
        const err = await actExcel.json().catch(() => ({}));
        showToast(err.detail || 'Fehler beim Aktivieren der Excel.');
        return;
      }

      // 6) Ggf. CSV in Session aktivieren (falls gefunden)
      if (matchedCsv && (matchedCsv.fileName || matchedCsv.name)) {
        const fdCsv = new FormData();
        fdCsv.append('file_name', matchedCsv.fileName || matchedCsv.name);
        try {
          const actCsv = await fetch(`${API_BASE}/csv/multi/activate`, {
            method: 'POST',
            headers: { 'Session-ID': sessionId },
            body: fdCsv,
          });
          if (!actCsv.ok) {
            const err = await actCsv.json().catch(() => ({}));
            console.warn('CSV-Aktivierung fehlgeschlagen:', err);
          }
        } catch (e) {
          console.warn('CSV-Aktivierung nicht möglich:', e);
        }
      } else {
        console.warn('Keine passende CSV zur Excel gefunden – Mapping-UI könnte fehlen.');
      }

      // 7) Warten bis BEIDE (csv + excel) in /session/data sichtbar sind
      try { setWaitingForExcel(true); } catch(_) {}
      const polled = await pollSessionForExcel(sessionId, 240, 1500); // ~6 Minuten
      try { setWaitingForExcel(false); } catch(_) {}
      if (!polled || !polled.csv || !polled.excel) {
        showToast('CSV/Excel nicht in der Session sichtbar.');
        return;
      }

      // 8) Wie im Einzel-Flow fortfahren (Mapping laden/anzeigen oder Auto-Skip)
      await handleMapping();
    } catch (e) {
      console.error('Ready-Check/Proceed Fehler:', e);
      showToast('Netzwerkfehler beim Fortfahren.');
    }
  };

  // Spalten, die standardmäßig auf "Formel" gesetzt werden sollen
  const preselectedHeaders = useMemo(() => ["JJ", "MM", "TT", "Kat", "SubKat", "Kst", "Kurzname", "GKonto", "Mwst", "MwstBetrag", "Netto", "EA-Art"], []);

  // Sammelt die aktuelle Zuordnung aus dem State und trimmt Werte
  const collectCurrentMappings = () => {
    const keys = (mappingData && Array.isArray(mappingData.excelHeaders) && mappingData.excelHeaders.length)
      ? mappingData.excelHeaders
      : Object.keys(finalMappings || {});
    const out = {};
    keys.forEach((k) => {
      let v = finalMappings && finalMappings[k] !== undefined ? finalMappings[k] : "";
      if (typeof v !== "string") v = String(v ?? "");
      v = v.trim();
      // Für bestimmte Felder, die standardmäßig "Formel" sein sollen
      if (!v && preselectedHeaders.includes(k)) v = "Formel";
      out[k] = v;
    });
    return out;
  };

  useEffect(() => {
    const sessionId = localStorage.getItem("sessionId");
    if (!sessionId) {
      setSessionError("Keine Session-ID gefunden. Bitte starten Sie eine neue Sitzung.");
      return;
    }

    const fetchSessionData = async () => {
      // prevent spamming the backend: allow a fetch at most once every 800ms
      const now = Date.now();
      if (now - (lastSessionFetchRef.current || 0) < 800) return;
      lastSessionFetchRef.current = now;
      try {
        const response = await fetch(`${API_BASE}/session/data`, {
          headers: { "Session-ID": sessionId },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setSessionError(errorData.detail || "Fehler beim Abrufen der Sitzungsdaten.");
          return;
        }

        const data = await response.json();
        if (data.csv && data.excel) {
          setCsvData(data.csv);
          setExcelData(data.excel);
          setIsAccountMatch(data.csv.konto === data.excel.account_number);

          // 🔁 Mapping nur einmal anfordern, sobald CSV + Excel vorhanden
          if (!isBatchMode && !mappingRequestedRef.current) {
            mappingRequestedRef.current = true;
            try {
              await handleMapping();
            } catch (e) {
              console.warn('handleMapping() failed:', e);
            }
          }
        } else if (data.csv && !data.excel) {
          // Excel noch nicht bereit → einmaliges, bewachtes Polling starten
          if (!pollingRef.current) {
            pollingRef.current = true;
            setWaitingForExcel(true);
            (async () => {
              try {
                const polled = await pollSessionForExcel(sessionId, 240, 1500); // ~6 Minuten bei 1.5s Intervall
                if (mountedRef.current && polled && polled.csv && polled.excel) {
                  setCsvData(polled.csv);
                  setExcelData(polled.excel);
                  setIsAccountMatch(polled.csv.konto === polled.excel.account_number);

                  // 🔁 Mapping nur einmal anfordern, sobald CSV + Excel nach Polling vorliegen
                  if (!isBatchMode && !mappingRequestedRef.current) {
                    mappingRequestedRef.current = true;
                    try {
                      await handleMapping();
                    } catch (e) {
                      console.warn('handleMapping() after poll failed:', e);
                    }
                  }
                }
              } finally {
                if (mountedRef.current) setWaitingForExcel(false);
                pollingRef.current = false;
              }
            })();
          }
        }
      } catch (error) {
        console.error("Fehler beim Abrufen der Sitzungsdaten:", error);
        setSessionError("Netzwerkfehler: Sitzungsdaten konnten nicht geladen werden.");
      }
    };

    fetchSessionData();
  }, [refreshTrigger, isBatchMode]);

  useEffect(() => {
    if (mappingData) {
      setFinalMappings((prevMappings) => {
        const updatedMappings = { ...prevMappings };
  
        mappingData.excelHeaders.forEach((header) => {
          // Falls das Feld in preselectedHeaders ist und noch nicht gesetzt wurde, setzen wir "Formel"
          if (!updatedMappings[header] && preselectedHeaders.includes(header)) {
            updatedMappings[header] = "Formel";
          }
        });
  
        return updatedMappings;
      });
    }
  }, [mappingData, preselectedHeaders]);


  const handleDownload = async () => {
    setShowPopup(false);

    const sessionId = localStorage.getItem("sessionId");
    if (!sessionId) return;

    try {
      setLoading(true);

      let blob = pendingBlob;

      if (!blob) {
        // Fallback: neu posten, falls kein Blob zwischengespeichert ist
        const payload = collectCurrentMappings();
        console.debug("[MappingPage] re-sending mappings (fallback) →", payload);
        const resp = await fetch(`${API_BASE}/session/insert_data`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Session-ID": sessionId,
          },
          body: safeStringify({ mappings: payload }),
        });
      if (!resp.ok) {
        const data = await resp.json();
        alert(data.detail || "Fehler beim Einfügen der Daten.");
        return;
      }
      blob = await resp.blob();
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "updated_file.xlsx";
    link.setAttribute("target", "_blank");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showToast(`Datei heruntergeladen: ${fileName || "updated_file.xlsx"}`);
    setDownloadComplete(true);
    setPendingBlob(null);
  } catch (error) {
    console.error("Fehler beim Speichern der Datei:", error);
    setSessionError("Netzwerkfehler: Datei konnte nicht gespeichert werden.");
  } finally {
    setLoading(false);
  }
};


  const ErrorBanner = sessionError ? (<div className="error">{sessionError}</div>) : null;
  return (
    <div className="mapping-page">
      {ErrorBanner}
      <h1>Datenmapping Übersicht</h1>

      <div className="view-toggle">
        <button
          className={isBatchMode ? "toggle-btn" : "toggle-btn active"}
          onClick={() => {
            modeLockedRef.current = false;
            try {
              localStorage.setItem('preferredViewMode','single');
              localStorage.setItem('navModeKey','1');
            } catch(e) {}
            setViewMode('single');
          }}
        >Einzel-Verarbeitung</button>
        <button
          className={isBatchMode ? "toggle-btn active" : "toggle-btn"}
          onClick={() => {
            // Force batch view and persist choice so navigation respects it
            modeLockedRef.current = true;
            try {
              localStorage.setItem('preferredViewMode', 'batch');
              localStorage.setItem('navModeKey', '2');
            } catch (e) {}
            setViewMode('batch');
          }}
        >Massenverarbeitung</button>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-box">
            <div className="loading-spinner"></div>
            <p>Daten werden geladen...</p>
          </div>
        </div>
      )}
      {waitingForExcel && (
        <div className="loading-overlay">
          <div className="loading-box">
            <div className="loading-spinner"></div>
            <p>Warte auf Excel-Daten …</p>
          </div>
        </div>
      )}
      {batchRunning && (
        <div className="loading-overlay">
          <div className="loading-box">
            <div className="loading-spinner"></div>
            <p>Massenverarbeitung läuft …</p>
            <div style={{ textAlign: 'left', marginTop: 12, maxHeight: 180, overflowY: 'auto', width: '100%', fontSize: 13 }}>
              <div><strong>Fortschritt:</strong> {batchProgress.done} / {batchProgress.total}</div>
              <ul style={{ marginTop: 6, paddingLeft: 18 }}>
                {(batchProgress.messages || []).slice(-8).map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
              <div style={{ marginTop: 8, height: 6, background: '#eee', borderRadius: 4 }}>
                <div style={{
                  width: (batchProgress.total ? (Math.min(batchProgress.done, batchProgress.total) / batchProgress.total) * 100 : 0) + '%',
                  height: '100%', borderRadius: 4, background: '#4caf50'
                }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {(!isBatchMode) && (csvData && excelData) ? (
        <>
          <div className="data-container">
            <div className="data-section">
              <h2>CSV-Daten</h2>
              <p><strong>Konto:</strong> {csvData.konto || "Nicht verfügbar"}</p>
              <p><strong>Anzahl der Zeilen:</strong> {csvData.total_rows || 0}</p>
              <p><strong>Neuestes Datum:</strong> {csvData.newest_date || "Nicht verfügbar"}</p>
              <p><strong>Ältestes Datum:</strong> {csvData.oldest_date || "Nicht verfügbar"}</p>
            </div>

            <div className="data-section">
              <h2>Excel-Daten</h2>
              <p><strong>Arbeitsblatt:</strong> {excelData.sheet_name || "Nicht verfügbar"}</p>
              <p><strong>Konto:</strong> {excelData.account_number || "Nicht verfügbar"}</p>
              <p><strong>Neuestes Datum:</strong> {excelData.newest_date || "Nicht verfügbar"}</p>
            </div>
          </div>

          <div className="comparison-section">
            {isAccountMatch ? (
              <div className="status success">✅ Kontonummern stimmen überein</div>
            ) : (
              <div className="status error">❌ Kontonummern stimmen nicht überein</div>
            )}
          </div>

          {isAccountMatch && (
            <div className="next-step">
              <button className="assign-button" onClick={handleUnifiedSubmit} disabled={loading}>
                {loading ? "Bitte warten…" : "Übermitteln"}
              </button>
              {autoSkipInfo && (
                <div className="status success" style={{ marginTop: 8 }}>{autoSkipInfo}</div>
              )}
            </div>
          )}

          {mappingData && !skipMappingUI && (
            <div className="mapping-fields">
              <p><strong>Vorgeschlagener Dateiname:</strong> {fileName}</p>
              <h3>Zuordnung der Überschriften</h3>

              {mappingData.excelHeaders.map((header, index) => (
                <div className="mapping-row" key={index}>
                  <p><strong>{header}</strong></p>
                  <select
                    value={finalMappings[header] || ""}
                    onChange={(e) => {
                      const val = (e.target.value || "").trim();
                      setFinalMappings((prev) => ({ ...prev, [header]: val }));
                    }}
                    name={`map-${header}`}
                  >
                    <option value="">-- CSV-Spalte auswählen --</option>
                    {mappingData.csvHeaders.map((csvHeader, idx) => (
                      <option key={idx} value={csvHeader}>{csvHeader}</option>
                    ))}
                  </select>
                </div>
              ))}
              <button className="finalize-button" onClick={handleFinalMapping}>
                Daten einfügen und herunterladen
              </button>{downloadComplete && (
  <button className="finish-button" onClick={() => navigate("/")}>
    Beenden
  </button>
)}
            </div>
          )}
        </>
      ) : (!isBatchMode ? (
        <p>Daten werden geladen...</p>
      ) : null)}

      {/* 📌 Popup-Fenster für Dateinamen */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup">
            <h2>Speicherort auswählen</h2>
            <label>
              Dateiname:
              <input
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
              />
            </label>
            <p className="safari-hint">
        <h3>💡 Falls der Speicherort nicht abgefragt wird:</h3>
        <ul>
          <li>Safari öffnen und zu den Einstellungen gehen (Cmd + ,)</li>
          <li>Tab „Allgemein“ auswählen</li>
          <li>„Dateien nach dem Download sichern in“ auf „Immer nachfragen“ ändern</li>
          <li>„Sichere Dateien nach dem Download öffnen“ deaktivieren</li>
        </ul>
        </p>
            <button className="finalize-button" onClick={handleDownload}>Speichern</button>
            <button className="finalize-button" onClick={() => setShowPopup(false)}>Abbrechen</button>
          </div>      
        </div>
        
      )}

      {toastMsg && (
        <div style={{
          position: "fixed",
          right: 16,
          bottom: 16,
          background: "rgba(0,0,0,0.8)",
          color: "#fff",
          padding: "10px 14px",
          borderRadius: 8,
          zIndex: 9999,
          maxWidth: 320,
          boxShadow: "0 4px 14px rgba(0,0,0,0.3)"
        }}>
          {toastMsg}
        </div>
      )}

      {/* ===== Massenverarbeitung (Nur in Batch-Ansicht) ===== */}
      {isBatchMode && (
        <div className="batch-panel" style={{marginTop: 28, padding: 16, border: '1px solid #e3e3e3', borderRadius: 10}}>
          <h2 style={{marginTop: 0}}>Massenverarbeitung</h2>
          <div style={{display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center'}}>
            <button className="assign-button" onClick={fetchBatchList} disabled={batchRunning}>
              {batchRunning ? 'Lade…' : 'Liste aktualisieren'}
            </button>
            <button className="assign-button" onClick={handleBatchProcess} disabled={batchRunning}>
              {batchRunning ? 'Verarbeite…' : 'Übermitteln'}
            </button>
          </div>
          {autoSkipInfo && (
            <div className="status success" style={{ marginTop: 8 }}>{autoSkipInfo}</div>
          )}
          {/* Neue Gruppentabelle: je Excel eine Vierer-Zeile (Excel, CSV, Details, Ergebnis) */}
          {batchResults && batchResults.length > 0 && (
            <div style={{marginTop: 14, overflowX: 'auto'}}>
              <table className="batch-grid-table">
                <thead>
                  <tr>
                    <th style={{width: '28%'}}>Excel</th>
                    <th style={{width: '28%'}}>CSV</th>
                    <th style={{width: '28%'}}>Details</th>
                    <th style={{width: '16%'}}>Ergebnis</th>
                  </tr>
                </thead>
                <tbody>
                  {batchResults.map((r, i) => {
                    const excelName = r.excel || r.excel_file || '-';
                    const csvName = r.csv || r.csv_file || '-';
                    const excelAcc = r.excel_account || r.excel_konto || r.excelKonto || '';
                    const csvAcc = r.csv_account || r.csv_konto || r.csvKonto || '';
                    const excelNewest = r.excel_newest || r.excelNewest || r.excel_neuestes_datum || '';
                    const csvNewest = r.csv_newest || r.csvNewest || r.csv_neuestes_datum || '';
                    const excelOldest = r.excel_oldest || r.excelOldest || r.excel_aeltestes_datum || '';
                    const csvOldest = r.csv_oldest || r.csvOldest || r.csv_aeltestes_datum || '';

                    const accountMatch = typeof r.account_match === 'boolean'
                      ? r.account_match
                      : (excelAcc && csvAcc && String(excelAcc) === String(csvAcc));

                    const mappingFound = r.mapping_found === true || r.mapping === 'found' || r.mapping === true;

                    const csvFound = !!csvName && csvName !== '-';

                    // Support row count for batch result summary
                    const rowCount = Number(r.rows || r.inserted_rows || r.rowCount || r.count || 0);

                    // Ein einzelnes Spalten-Icon
                    const Icon = ({ok}) => (
                      <span className={ok ? 'ok-icon' : 'err-icon'}>{ok ? '✔' : '✖'}</span>
                    );

                    return (
                      <React.Fragment key={i}>
                        {/* Zeile 1: Excel-Datei */}
                        <tr className="group-row top">
                          <td className="mono ellipsis" title={excelName}>{excelName}</td>
                          <td className="muted">zugehörige CSV</td>
                          <td className="muted">–</td>
                          <td className="muted">–</td>
                        </tr>
                        {/* Zeile 2: CSV-Datei */}
                        <tr className="group-row">
                          <td className="muted">CSV-Datei</td>
                          <td className="mono ellipsis" title={csvName}>{csvName}</td>
                          <td className="muted">–</td>
                          <td><Icon ok={csvFound} /></td>
                        </tr>
                        {/* Zeile 3: Details (Kontonummern & Daten) */}
                        <tr className="group-row">
                          <td>
                            <div><strong>Konto (Excel):</strong> {excelAcc || '-'}</div>
                            <div><strong>Neu / Alt:</strong> {excelNewest || '-'} / {excelOldest || '-'}</div>
                          </td>
                          <td>
                            <div><strong>Konto (CSV):</strong> {csvAcc || '-'}</div>
                            <div><strong>Neu / Alt:</strong> {csvNewest || '-'} / {csvOldest || '-'}</div>
                          </td>
                          <td>
                            <div><strong>Mapping vorhanden:</strong> {mappingFound ? 'Ja' : 'Nein'}</div>
                            <div><strong>Match Konto:</strong> {accountMatch ? 'Ja' : 'Nein'}</div>
                          </td>
                          <td className="icons-col">
                            <div><Icon ok={mappingFound} /> Mapping</div>
                            <div><Icon ok={accountMatch} /> Konto</div>
                          </td>
                        </tr>
                        {/* Zeile 4: Gesamtergebnis */}
                        <tr className="group-row bottom">
                          <td colSpan={3} className="muted">Gesamt</td>
                          <td>
                            <span className={ (csvFound && mappingFound && accountMatch) ? 'badge ok' : 'badge err' }>
                              {(csvFound && mappingFound && accountMatch) ? 'Bereit' : 'Offen'}
                            </span>
                            <div className="muted small" style={{ marginTop: 4 }}>
                              {rowCount > 0 ? `${rowCount} Zeilen` : '–'}
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {isBatchMode && mappingData && !skipMappingUI && (
        <div className="mapping-fields">
          <p><strong>Vorgeschlagener Dateiname:</strong> {fileName}</p>
          <h3>Zuordnung der Überschriften</h3>

          {mappingData.excelHeaders.map((header, index) => (
            <div className="mapping-row" key={index}>
              <p><strong>{header}</strong></p>
              <select
                value={finalMappings[header] || ""}
                onChange={(e) => {
                  const val = (e.target.value || "").trim();
                  setFinalMappings((prev) => ({ ...prev, [header]: val }));
                }}
                name={`map-${header}`}
              >
                <option value="">-- CSV-Spalte auswählen --</option>
                {mappingData.csvHeaders.map((csvHeader, idx) => (
                  <option key={idx} value={csvHeader}>{csvHeader}</option>
                ))}
              </select>
            </div>
          ))}
          <button className="finalize-button" onClick={handleFinalMapping}>
            Daten einfügen und herunterladen
          </button>
          {downloadComplete && (
            <button className="finish-button" onClick={() => navigate("/")}>
              Beenden
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MappingPage;